import { createContext, useContext, useMemo, type PropsWithChildren } from 'react';
import { useDecision } from '@optimizely/react-sdk';
import isEmpty from 'lodash/isEmpty';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUserContext } from '@fff-web/fff-utilities';
import { GROWTH_MY_REACTIVATE_MEMBERSHIP } from '../../../utils/Optimizely/featureFlagVariations';
import { redirectToBaseSite } from '../../../utils/redirectMethods';
import defaultReactivateMyMembershipContext, {
  REACTIVATE_MEMBERSHIP_EVERGREEN_CONTENT,
} from './defaultReactivateMyMembershipContext';

export const ReactivateMyMembershipContext = createContext(defaultReactivateMyMembershipContext);

export const ReactivateMyMembershipProvider = ({ children }: PropsWithChildren) => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const { userInfo } = useUserContext();
  const [growthMyMembershipDecision] = useDecision(GROWTH_MY_REACTIVATE_MEMBERSHIP.FEATURE_FLAG);

  const variables = !isEmpty(growthMyMembershipDecision.variables)
    ? growthMyMembershipDecision.variables
    : REACTIVATE_MEMBERSHIP_EVERGREEN_CONTENT;

  const memoizedValues = useMemo(
    () => ({ growthMyMembership: { ...growthMyMembershipDecision, variables } }),
    [growthMyMembershipDecision, variables],
  );

  const subscriptionState = userInfo?.user?.subscriptionState;
  const { loggedIn } = userInfo;

  if (loggedIn && subscriptionState === 'CANCELED') {
    navigate(`/reactivate-today${search}`, { replace: true });
    return null;
  }

  if (loggedIn && subscriptionState !== 'EXPIRED') {
    redirectToBaseSite();
    return null;
  }

  return (
    <ReactivateMyMembershipContext.Provider value={memoizedValues}>
      {children}
    </ReactivateMyMembershipContext.Provider>
  );
};

export function useReactivateMyMembershipContext() {
  const context = useContext(ReactivateMyMembershipContext);
  if (!context) {
    throw new Error(
      'You must use the ReactivateMyMembershipProvider in order to consume this context.',
    );
  }
  return context;
}

export default ReactivateMyMembershipProvider;
